import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdatePartlyCompletedMachine } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { cn } from '@/shared/lib/utils'

type OnboardingMachineCompletionStepProps = {
  complianceId: string
  isPartlyCompletedMachine?: boolean | null
  onContinue: () => void
}

type SelectionValue = 'true' | 'false'

const MACHINE_COMPLETION_OPTIONS: {
  value: SelectionValue
  labelKey:
    | 'onboarding.machineCompletion.noAnswer'
    | 'onboarding.machineCompletion.yesAnswer'
}[] = [
  {
    value: 'false',
    labelKey: 'onboarding.machineCompletion.noAnswer',
  },
  {
    value: 'true',
    labelKey: 'onboarding.machineCompletion.yesAnswer',
  },
]

function toSelectionValue(
  isPartlyCompletedMachine: boolean | null | undefined
): SelectionValue | null {
  if (isPartlyCompletedMachine == null) {
    return null
  }
  return isPartlyCompletedMachine ? 'true' : 'false'
}

export function OnboardingMachineCompletionStep({
  complianceId,
  isPartlyCompletedMachine = null,
  onContinue,
}: OnboardingMachineCompletionStepProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const updatePartlyCompletedMachineMutation =
    useUpdatePartlyCompletedMachine(complianceId)
  const isPending = updatePartlyCompletedMachineMutation.isPending
  const [selectedValue, setSelectedValue] = useState<SelectionValue | null>(
    () => toSelectionValue(isPartlyCompletedMachine)
  )

  useEffect(() => {
    setSelectedValue(toSelectionValue(isPartlyCompletedMachine))
  }, [isPartlyCompletedMachine])

  function handleContinue() {
    if (selectedValue == null) {
      return
    }

    const isPartlyCompleted = selectedValue === 'true'
    if (
      isPartlyCompletedMachine != null &&
      isPartlyCompletedMachine === isPartlyCompleted
    ) {
      onContinue()
      return
    }

    updatePartlyCompletedMachineMutation.mutate(
      { isPartlyCompletedMachine: isPartlyCompleted },
      { onSuccess: onContinue }
    )
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <div className='flex min-h-0 flex-1 flex-col gap-6'>
        <div className='space-y-4'>
          <h2 className='text-lg font-semibold tracking-tight'>
            {t('onboarding.machineCompletion.question')}
          </h2>
          <p className='leading-relaxed text-muted-foreground'>
            {t('onboarding.machineCompletion.description')}
          </p>
        </div>

        <RadioGroup
          value={selectedValue ?? ''}
          onValueChange={(value) => {
            if (value === 'true' || value === 'false') {
              setSelectedValue(value)
            }
          }}
          disabled={isPending}
          aria-label={t('onboarding.machineCompletion.question')}
          className='grid w-full gap-3'
        >
          {MACHINE_COMPLETION_OPTIONS.map((option) => {
            const itemId = `onboarding-machine-completion-${option.value}`
            return (
              <Label
                key={option.value}
                htmlFor={itemId}
                className={cn(
                  'flex w-full cursor-pointer items-start gap-3 rounded-md border border-border p-4 text-left font-normal transition-colors',
                  'hover:bg-muted/50 focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
                  'has-[[data-slot=radio-group-item][data-checked]]:border-primary/50 has-[[data-slot=radio-group-item][data-checked]]:bg-primary/5'
                )}
              >
                <RadioGroupItem
                  value={option.value}
                  id={itemId}
                  className='mt-0.5'
                />
                <span className='text-sm leading-snug font-medium'>
                  {t(option.labelKey)}
                </span>
              </Label>
            )
          })}
        </RadioGroup>
      </div>

      <div className='mt-6 flex shrink-0 justify-end pt-6'>
        <Button
          type='button'
          disabled={isPending || selectedValue == null}
          onClick={handleContinue}
        >
          {t('onboarding.intro.continue')}
        </Button>
      </div>
    </div>
  )
}
